// Default component properties, defined for :host. Properties which can not
// travel the shadow boundary are defined through this mixin, available for shared
// and default mode
@include host-component-properties;

// ----------------------------------------------------------------------------------------------------
// Bullet Mixins: Achieve the styling of a bullet
// ----------------------------------------------------------------------------------------------------
@mixin pearl-chain-bullet {
  content: "";
  position: absolute;
  inset-block-start: 0;
  background: currentcolor;
  color: inherit;
  border-color: currentcolor;
  border-radius: 100%;
}

@mixin pearl-chain-bullet-start-end {
  height: px-to-rem(var(--paf-pearl-chain-bullet-size-start-end));
  width: px-to-rem(var(--paf-pearl-chain-bullet-size-start-end));
}

@mixin pearl-chain-bullet-stop {
  min-height: px-to-rem(var(--paf-pearl-chain-bullet-size-stop));
  min-width: px-to-rem(var(--paf-pearl-chain-bullet-size-stop));
  height: px-to-rem(var(--paf-pearl-chain-bullet-size-stop));
  width: px-to-rem(var(--paf-pearl-chain-bullet-size-stop));
  border: px-to-rem(var(--paf-pearl-chain-bullet-border-width)) solid currentcolor;
  background: Canvas;
  z-index: 2;
  transform: translateY(calc(-50% + px-to-rem(var(--paf-pearl-chain-height) / 2)));
}

@mixin pearl-chain-bullet-disruption {
  --paf-pearl-chain-bullet-color: var(--paf-pearl-chain-bullet-color-disruption);

  @include forced-colors {
    --paf-pearl-chain-bullet-color: LinkText;
  }
}

@mixin pearl-chain-bullet-skipped {
  border: px-to-rem(var(--paf-pearl-chain-bullet-border-width)) solid var(--paf-pearl-chain-bullet-color);
  background: Canvas;

  &:before {
    content: "";
    position: absolute;
    inset-block-start: 50%;
    inset-inline-start: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
    border-block-start: var(--paf-pearl-chain-bullet-crossed-border-width) solid canvas;
    background: var(--paf-pearl-chain-bullet-color-disruption);
    height: px-to-rem(var(--paf-pearl-chain-bullet-crossed-height));
    width: px-to-rem(var(--paf-pearl-chain-bullet-crossed-width));

    @include forced-colors {
      background: var(--paf-pearl-chain-color-disruption);
    }
  }
}

@mixin pearl-chain-bullet-skipped-stop {
  @include pearl-chain-bullet-stop;
  @include pearl-chain-bullet-skipped;
}

@mixin pearl-chain-bullet-position {
  @include pearl-chain-bullet-start-end;
  @include pearl-chain-bullet;

  & {
    @include pearl-chain-bullet-disruption;

    animation:
      pearl-chain-bullet-position-pulse
      var(--paf-pearl-chain-bullet-animation-duration)
      var(--paf-pearl-chain-bullet-animation-easing)
      infinite;
  }

  @keyframes pearl-chain-bullet-position-pulse {
    0% {
      box-shadow: 0 0 0 0 var(--paf-pearl-chain-bullet-color-disruption);
    }

    25% {
      box-shadow: 0 0 0 0 var(--paf-pearl-chain-bullet-color-disruption);
    }

    50% {
      box-shadow: 0 0 0 #{px-to-rem(3)} var(--sbb-color-red-alpha-20); // TODO exchange
    }

    100% {
      box-shadow: 0 0 0 0 var(--paf-pearl-chain-bullet-color-disruption);
    }
  }
}

@mixin pearl-chain-dotted {
  background-color: unset;
  background-image: linear-gradient(to right, currentcolor 0%, currentcolor 50%, transparent 50%);
  background-repeat: repeat-x;
  background-size:
    calc(2 * px-to-rem(var(--paf-pearl-chain-spacing-small)))
    px-to-rem(var(--paf-pearl-chain-height));

  .pearl-chain--vertical & {
    background-image: linear-gradient(to bottom, currentcolor 0%, currentcolor 50%, transparent 50%);
    background-repeat: repeat-y;
    background-size:
      px-to-rem(var(--paf-pearl-chain-height))
      calc(2 * px-to-rem(var(--paf-pearl-chain-spacing-small)));
  }

  @include forced-colors {
    background: unset;
    border-block-start: px-to-rem(1) dashed LinkText;
    transform: translateY(px-to-rem(1));
  }
}

:host {
  display: flex;

  --paf-pearl-chain-bullet-size-start-end: 10;
  --paf-pearl-chain-bullet-size-stop: 8;
  --paf-pearl-chain-bullet-color: var(--paf-global-post-brand-black);
  --paf-pearl-chain-bullet-color-past: var(--paf-global-post-brand-secondary);
  --paf-pearl-chain-bullet-color-irrelevant: var(--sbb-color-metal);
  --paf-pearl-chain-bullet-color-disruption: var(--paf-global-sbb-brand-red);
  --paf-pearl-chain-bullet-border-width: var(--paf-global-border-width-2x);
  --paf-pearl-chain-bullet-animation-duration: 1920ms;
  --paf-pearl-chain-bullet-animation-easing: ease-out;
  --paf-pearl-chain-bullet-crossed-width: 14.14;
  --paf-pearl-chain-bullet-crossed-height: 3.5;
  --paf-pearl-chain-bullet-crossed-border-width: #{px-to-rem(1.5)};
  --paf-pearl-chain-height: var(--paf-global-border-width-2x);
  --paf-pearl-chain-spacing-small: 2;
  --paf-pearl-chain-color: var(--paf-pearl-chain-bullet-color);
  --paf-pearl-chain-color-disruption: var(--paf-pearl-chain-bullet-color-disruption);
  --paf-pearl-chain-color-past: var(--paf-pearl-chain-bullet-color-past);
  --paf-pearl-chain-leg-width: 100%;

  @include forced-colors {
    --paf-pearl-chain-color: CanvasText;
    --paf-pearl-chain-bullet-color-past: GrayText;
    --paf-pearl-chain-color-past: GrayText;
    --paf-pearl-chain-color-disruption: LinkText;
  }
}

// ----------------------------------------------------------------------------------------------------
// The pearl chain wrapper
// ----------------------------------------------------------------------------------------------------
.pearl-chain {
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  width: 100%;
  color: var(--paf-pearl-chain-color);
  padding-block:
    calc(
      (
        px-to-rem(var(--paf-pearl-chain-bullet-size-start-end)) - px-to-rem(var(--paf-pearl-chain-height))
      ) / 2
    );
  padding-inline: #{px-to-rem(var(--paf-pearl-chain-bullet-size-start-end))};
}

// Entire Journey Cancelled
.pearl-chain--disruption {
  @include pearl-chain-bullet-disruption;
}

.pearl-chain--vertical {
  flex: 1 1 100%;
  flex-direction: column;
  writing-mode: vertical-lr;
  width: px-to-rem(10);
  overflow: hidden;
}

// ----------------------------------------------------------------------------------------------------
// The bullets - start and the end of the pearl chain
// ----------------------------------------------------------------------------------------------------
.pearl-chain__bullet {
  @include pearl-chain-bullet;
  @include pearl-chain-bullet-start-end;

  @include forced-colors {
    background-color: var(--paf-pearl-chain-color);

    .pearl-chain--past & {
      background-color: var(--paf-pearl-chain-color-past);
    }
  }
}

.pearl-chain__bullet--start {
  inset-inline-start: 0;
}

.pearl-chain__bullet--end {
  inset-inline-end: 0;
  z-index: 3;
}

.pearl-chain:has(.pearl-chain__leg--disruption:first-of-type) .pearl-chain__bullet--start,
.pearl-chain:has(.pearl-chain__leg--disruption:last-of-type) .pearl-chain__bullet--end {
  color: var(--paf-pearl-chain-color-disruption);

  @include forced-colors {
    background-color: var(--paf-pearl-chain-color-disruption);
  }
}

.pearl-chain:has(.pearl-chain__leg--past:first-of-type) .pearl-chain__bullet--start,
.pearl-chain:has(.pearl-chain__leg--past:last-of-type) .pearl-chain__bullet--end {
  color: var(--paf-pearl-chain-color-past);

  @include forced-colors {
    background-color: var(--paf-pearl-chain-color-past);
  }
}

.pearl-chain__bullet--arrival-skipped,
.pearl-chain__bullet--departure-skipped {
  @include pearl-chain-bullet-start-end;
  @include pearl-chain-bullet-skipped;
}

// ----------------------------------------------------------------------------------------------------
// The legs - everything happening in between the dots
// ----------------------------------------------------------------------------------------------------
.pearl-chain__leg {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-grow: 0;
  height: px-to-rem(var(--paf-pearl-chain-height));
  border-inline-end: px-to-rem(var(--paf-pearl-chain-spacing-small)) solid transparent;
  width: var(--paf-pearl-chain-leg-width);
  align-items: center;

  .pearl-chain--vertical & {
    height: 100%;
    width: px-to-rem(var(--paf-pearl-chain-height));
  }
}

.pearl-chain__leg:last-of-type {
  border: none;
}

// line on leg
.pearl-chain__leg:after {
  content: "";
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: px-to-rem(var(--paf-pearl-chain-spacing-small));
  inset-block-end: 0;
  inset-inline-start: 0;
  background-color: currentcolor;
  border-radius: px-to-rem(var(--paf-pearl-chain-border-height));

  @include forced-colors {
    background-color: var(--paf-pearl-chain-bullet-color-past);

    .pearl-chain--past & {
      background-color: var(--paf-pearl-chain-bullet-color-past);
    }
  }
}

.pearl-chain__leg:last-of-type:after {
  inset-inline-end: calc(-1 * px-to-rem(var(--paf-pearl-chain-height)));
}

// Cancelled segment
.pearl-chain__leg--disruption,
.pearl-chain__leg:has(.pearl-chain__stop--departure-skipped),
.pearl-chain__leg:has(.pearl-chain__stop--arrival-skipped) {
  color: var(--paf-pearl-chain-color-disruption);

  &:after {
    @include pearl-chain-dotted;
  }
}

.pearl-chain__leg--past {
  color: var(--paf-pearl-chain-color-past);
}

.pearl-chain__leg .pearl-chain__stop--departure-skipped {
  @include pearl-chain-bullet-skipped-stop;
}

// This is a tricky one. If the arrival of a leg is skipped,
// we have to find the pearl-chain__stop of the next leg and
// add the special styling
.pearl-chain__leg:has(.pearl-chain__stop--arrival-skipped) {
  color: var(--paf-pearl-chain-bullet-color);

  &:after {
    color: var(--paf-pearl-chain-color-disruption);
  }

  & + .pearl-chain__leg .pearl-chain__stop {
    @include pearl-chain-bullet-skipped-stop;
  }
}

// ----------------------------------------------------------------------------------------------------
// The bullets - between start and end
// ----------------------------------------------------------------------------------------------------
.pearl-chain__stop {
  @include pearl-chain-bullet;
  @include pearl-chain-bullet-stop;
}

.pearl-chain__leg:first-of-type .pearl-chain__stop {
  display: none;
}
